import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check as CheckIcon, CreditCard, FileText, LogOut, Package, Pause, Pencil, Play, ShieldCheck, ShoppingCart, Sparkles, Trash2, X } from "lucide-react";
import Layout from "@/components/Layout";
import SubscriptionAdjuster from "@/components/SubscriptionAdjuster";
import { deliveryWindowLabel } from "@/lib/delivery-dates";
import { supabase } from "@/integrations/supabase/client";
import { getExactTier, products as PRODUCTS } from "@/data/pricing";
import { useCheckout, type ResumableOrder } from "@/contexts/CheckoutContext";
import { getStripe } from "@/lib/stripe";
import { downloadInvoicePdf } from "@/lib/invoice-pdf";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "God morgon,";
  if (h < 18) return "God eftermiddag,";
  return "God kväll,";
};

type View = "overview" | "adjust";

type Subscription = {
  id: string;
  status: string;
  tier_tubes: number;
  quantities: Record<string, number>;
  monthly_price_cents: number | null;
  next_delivery_date: string | null;
  currency: string | null;
  paused_until: string | null;
  cancel_at_period_end: boolean | null;
  current_period_end: string | null;
  paused_for_overdue: boolean | null;
  paused_for_overdue_invoice_id: string | null;
  nickname: string | null;

  pending_quantities: Record<string, number> | null;
  pending_tier_tubes: number | null;
  pending_monthly_price_cents: number | null;
  pending_effective_date: string | null;
  discount_id: string | null;
  discount_type_snapshot: "percent" | "fixed_amount" | null;
  discount_value_snapshot: number | null;
  discount_duration_snapshot: "once" | "forever" | null;
  discount?: { code: string | null; name: string } | null;
  is_pilot?: boolean;
};

type Order = {
  id: string;
  created_at: string;
  status: string;
  payment_status: string | null;
  payment_method: string | null;
  total_cents: number | null;
  currency: string | null;
  receipt_url: string | null;
  product_name: string;
  order_mode: string | null;
  quantity: number;
  items: Array<{ product_id: string; quantity: number }> | null;
  shipping_address: ResumableOrder["shipping_address"] | null;
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("sv-SE", { year: "numeric", month: "short", day: "numeric" });

const formatLongDate = (iso: string) =>
  new Date(iso).toLocaleDateString("sv-SE", { year: "numeric", month: "long", day: "numeric" });

// Combined order status used on the account page. Returns a primary badge
// (label + colour) plus an optional small grey subtext explaining *why*
// (e.g. "Faktura skickad" vs "Betald") so the customer always knows the
// real state — confirmed orders are framed as "Order hanteras" rather than
// just "Betald", since payment alone doesn't mean fulfilled.
const formatOrderState = (
  status: string | null,
  paymentStatus: string | null,
  paymentMethod: string | null,
): { label: string; className: string; subtext?: string } => {
  // Shipped is highest-priority — once we've sent it, that's what the
  // customer sees regardless of how they paid.
  if (status === "shipped") {
    return { label: "Skickad", className: "bg-emerald-100 text-emerald-800" };
  }

  switch (paymentStatus) {
    case "paid":
      return {
        label: "Order hanteras",
        className: "bg-emerald-100 text-emerald-800",
        subtext: "Betald",
      };
    case "scheduled":
      // Future subscription delivery — not yet billed/paid
      return {
        label: "Planerad leverans",
        className: "bg-slate-100 text-slate-700",
        subtext:
          paymentMethod === "invoice"
            ? "Faktura skickas vid leverans"
            : "Kortet dras vid leverans",
      };
    case "pending":
      return {
        label: "Order hanteras",
        className: "bg-emerald-100 text-emerald-800",
        subtext: paymentMethod === "invoice" ? "Faktura skickad" : "Väntar på betalning",
      };
    case "awaiting_payment":
      return { label: "Slutför betalning", className: "bg-amber-100 text-amber-800" };
    case "requires_action":
      return {
        label: "Bekräfta betalning",
        className: "bg-orange-100 text-orange-800",
        subtext: "Banken kräver extra säkerhetskontroll",
      };
    case "failed":
      return {
        label: "Betalning misslyckades",
        className: "bg-red-100 text-red-800",
        subtext: paymentMethod === "card" ? "Uppdatera kortuppgifter" : "Försök igen",
      };
    case "cancelled":
      return { label: "Avbruten", className: "bg-slate-100 text-slate-600" };
    case "paused":
      return {
        label: "Pausad",
        className: "bg-blue-100 text-blue-800",
        subtext: "Återupptas när prenumerationen aktiveras igen",
      };
    case "refunded":
      return { label: "Återbetald", className: "bg-slate-100 text-slate-800" };
    case "partially_refunded":
      return { label: "Delvis återbetald", className: "bg-slate-100 text-slate-800" };
    default:
      return { label: paymentStatus || "—", className: "bg-slate-100 text-slate-800" };
  }
};

const statusBadge = (status: string, cancelAtPeriodEnd?: boolean | null) => {
  if (cancelAtPeriodEnd && status !== "cancelled") {
    return { label: "Uppsagd", className: "bg-rose-100 text-rose-800" };
  }
  switch (status) {
    case "active":
      return { label: "Aktiv", className: "bg-emerald-100 text-emerald-800" };
    case "paused":
      return { label: "Pausad", className: "bg-amber-100 text-amber-800" };
    case "cancelled":
      return { label: "Avslutad", className: "bg-rose-100 text-rose-800" };
    default:
      return { label: status, className: "bg-slate-100 text-slate-800" };
  }
};

type CustomerInvoice = {
  id: string;
  number: string | null;
  status: string;
  amount_cents: number | null;
  currency: string | null;
  due_date: string | null;
  ocr_reference: string | null;
  hosted_invoice_url: string | null;
  created_at: string;
  stripe_status: string | null;
  billing_reason: string | null;
  period_start: string | null;
  period_end: string | null;
  subscription_id: string | null;
};

const invoiceBadge = (status: string, overdue = false) => {
  if (overdue && (status === "open" || status === "uncollectible")) {
    return { label: "Förfallen", className: "bg-rose-100 text-rose-800" };
  }
  switch (status) {
    case "paid":
      return { label: "Betald", className: "bg-emerald-100 text-emerald-800" };
    case "open":
      return { label: "Att betala", className: "bg-amber-100 text-amber-800" };
    case "draft":
      return { label: "Kommande", className: "bg-slate-100 text-slate-700" };
    case "void":
      return { label: "Makulerad", className: "bg-rose-100 text-rose-800" };
    case "uncollectible":
      return { label: "Förfallen", className: "bg-rose-100 text-rose-800" };
    default:
      return { label: status, className: "bg-slate-100 text-slate-800" };
  }
};

const invoicePeriodLabel = (inv: CustomerInvoice) => {
  if (!inv.subscription_id && !inv.billing_reason?.startsWith("subscription")) return null;
  if (!inv.period_start) return "Abonnemang";
  const d = new Date(`${inv.period_start}T00:00:00`);
  return `Abonnemang · ${d.toLocaleDateString("sv-SE", { month: "long", year: "numeric" })}`;
};



const AccountPage = () => {
  const navigate = useNavigate();
  const { resumeCheckout } = useCheckout();
  const [user, setUser] = useState<{ id: string; email: string; firstName?: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>("overview");
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [invoices, setInvoices] = useState<CustomerInvoice[]>([]);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<string | null>(null);

  const [isAdmin, setIsAdmin] = useState(false);

  // Action dialogs
  const [pauseOpen, setPauseOpen] = useState(false);
  // YYYY-MM-DD = LAST day of pause month (i.e. month BEFORE chosen resume month)
  const [pausedUntil, setPausedUntil] = useState<string>("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  // Locks the "Slutför betalning"/"Försök igen" button per order while the
  // checkout sheet is opening, so a double-click can never trigger two
  // parallel sessions (which previously caused duplicate awaiting_payment
  // drafts). Cleared after a short delay since the sheet handles the rest.
  const [resumingOrderId, setResumingOrderId] = useState<string | null>(null);
  const [supportOrderId, setSupportOrderId] = useState<string>("");
  const [supportTopic, setSupportTopic] = useState<string>("Återbetalning");
  const [supportMessage, setSupportMessage] = useState<string>("");
  const [supportSending, setSupportSending] = useState(false);

  const sendSupportRequest = async () => {
    if (!user || supportSending) return;
    if (supportMessage.trim().length < 5) {
      toast.error("Beskriv ditt ärende lite mer utförligt");
      return;
    }
    setSupportSending(true);
    try {
      const sel = orders.find((o) => o.id === supportOrderId);
      const orderLabel = sel
        ? `${sel.product_name} · ${formatDate(sel.created_at)} · ${sel.total_cents != null ? `${(sel.total_cents / 100).toFixed(0)} ${sel.currency || "SEK"}` : "—"}`
        : undefined;
      const { data, error } = await (supabase as any).functions.invoke("submit-support-request", {
        body: {
          topic: supportTopic,
          orderLabel,
          message: supportMessage.trim(),
        },
      });
      if (error || data?.error) throw new Error(data?.error ?? error?.message ?? "Kunde inte skicka");
      toast.success("Tack! Vi återkommer till dig så snart vi kan.");
      setSupportMessage("");
      setSupportOrderId("");
    } catch (e: any) {
      toast.error(e?.message || "Kunde inte skicka meddelandet");
    } finally {
      setSupportSending(false);
    }
  };

  // Inline nickname edit (per active subscription)
  const [editingNickname, setEditingNickname] = useState(false);
  const [nicknameDraft, setNicknameDraft] = useState("");

  const saveNickname = async () => {
    if (!subscription) return;
    const trimmed = nicknameDraft.trim().slice(0, 80);
    const value = trimmed || null;
    const { error } = await (supabase as any).rpc("update_subscription_nickname", {
      _subscription_id: subscription.id,
      _nickname: value,
    });
    if (error) {
      toast.error("Kunde inte spara namnet");
      return;
    }
    setEditingNickname(false);
    if (user) await fetchSubscription(user.id);
    toast.success("Namnet är uppdaterat");
  };

  const fetchOrders = async (userId: string) => {
    const { data: ords } = await (supabase as any)
      .from("orders")
      .select("id, created_at, status, payment_status, payment_method, total_cents, currency, receipt_url, product_name, order_mode, quantity, items, shipping_address")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(20);
    if (ords) {
      // Hide free pilot-package orders entirely from the customer's order
      // history. They have total_cents = 0 (set by admin-create-subscription
      // when is_pilot = true) and the customer never paid for them — showing
      // them with a "Försök igen"-knapp after the pilot is cancelled is
      // confusing. Admin views still show them for shipping management.
      const visible = (ords as Order[]).filter(
        (o) => Number(o.total_cents ?? 0) > 0,
      );
      setOrders(visible);
    }
  };

  const fetchInvoices = async (userId: string) => {
    const { data } = await (supabase as any)
      .from("invoices")
      .select("id, number, status, amount_cents, currency, due_date, ocr_reference, hosted_invoice_url, created_at, stripe_status, billing_reason, period_start, period_end, subscription_id")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(24);
    if (data) setInvoices(data as CustomerInvoice[]);
  };

  const handleDownloadInvoice = async (inv: CustomerInvoice) => {
    setDownloadingInvoiceId(inv.id);
    try {
      const voided = (inv.stripe_status || inv.status) === "void";
      await downloadInvoicePdf(inv.id, inv.number, { voided });
      if (voided) {
        toast.warning("Den här fakturan är makulerad och ska inte betalas.");
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Kunde inte ladda ner fakturan");
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  const refreshAll = async (userId: string) => {
    await Promise.all([fetchSubscription(userId), fetchOrders(userId), fetchInvoices(userId)]);
  };


  const fetchSubscription = async (userId: string) => {
    const { data } = await (supabase as any)
      .from("subscriptions")
      .select(
        "id, status, tier_tubes, quantities, monthly_price_cents, next_delivery_date, currency, paused_until, cancel_at_period_end, current_period_end, paused_for_overdue, paused_for_overdue_invoice_id, nickname, pending_quantities, pending_tier_tubes, pending_monthly_price_cents, pending_effective_date, discount_id, discount_type_snapshot, discount_value_snapshot, discount_duration_snapshot, is_pilot",
      )

      .eq("user_id", userId)
      .in("status", ["active", "paused"])
      .order("created_at", { ascending: true });
    const list = (data as Subscription[] | null) || [];
    // Fetch discount labels for any sub with a discount_id (best-effort)
    for (const sub of list) {
      if (sub.discount_id) {
        // Use security_invoker view that excludes Stripe IDs
        const { data: d } = await (supabase as any)
          .from("subscription_discount_view")
          .select("code, name")
          .eq("id", sub.discount_id)
          .maybeSingle();
        if (d) sub.discount = d as { code: string | null; name: string };
      }
    }
    setSubscriptions(list);
    setSubscription(list[0] || null);
  };

  useEffect(() => {
    const init = async () => {
      const { data: { user: u } } = await supabase.auth.getUser();
      if (!u) {
        navigate("/login");
        return;
      }
      setUser({
        id: u.id,
        email: u.email || "",
        firstName: (u.user_metadata?.first_name as string | undefined) || undefined,
      });

      await fetchSubscription(u.id);

      // Server-side admin check via security-definer RPC. Never trust client-only state.
      const { data: adminCheck, error: adminErr } = await supabase.rpc("has_role", {
        _user_id: u.id,
        _role: "admin",
      });
      setIsAdmin(!adminErr && adminCheck === true);

      await Promise.all([fetchOrders(u.id), fetchInvoices(u.id)]);

      setLoading(false);

      // Show toast after Stripe Checkout setup-mode return
      const params = new URLSearchParams(window.location.search);
      if (params.get("card_updated") === "1") {
        toast.success("Kortuppgifterna har uppdaterats");
        window.history.replaceState({}, "", "/account");
      }
    };
    init();
  }, [navigate]);

  // Realtime: subscription + orders updates → keep portal in sync without reload
  useEffect(() => {
    if (!user?.id) return;
    const userId = user.id;
    // Private channel → Realtime enforces our RLS policy on realtime.messages,
    // which restricts each user to their own `account-live-{uid}` topic.
    const channel = supabase
      .channel(`account-live-${userId}`, { config: { private: true } })
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "subscriptions", filter: `user_id=eq.${userId}` },
        () => {
          fetchSubscription(userId);
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "orders", filter: `user_id=eq.${userId}` },
        () => {
          fetchOrders(userId);
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "invoices", filter: `user_id=eq.${userId}` },
        () => {
          fetchInvoices(userId);
        },
      )

      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const handlePause = async () => {
    if (!subscription) return;
    if (!pausedUntil) {
      toast.error("Välj en månad då leveranserna ska återupptas");
      return;
    }
    setActionBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("pause-subscription", {
        body: {
          subscription_id: subscription.id,
          paused_until: pausedUntil,
        },
      });
      if (error || !data?.success) throw new Error(error?.message || data?.error || "Misslyckades");
      const resumeDate = data?.next_delivery_date as string | undefined;
      const retained = Boolean(data?.retained_delivery);
      const voided = Number(data?.voided_invoices ?? 0) > 0;
      // Refresh state immediately so the portal reflects the new pause without
      // waiting for the realtime event (and works even if realtime is delayed).
      if (user) await refreshAll(user.id);
      toast.success(
        retained
          ? "Pausad. Din redan betalda leverans skickas som planerat."
          : voided
            ? "Pausad. Den obetalda fakturan för den här månaden är annullerad."
            : resumeDate
              ? `Pausad. Nästa leverans skickas ${formatLongDate(resumeDate)}.`
              : "Prenumerationen är pausad",
      );

      setPauseOpen(false);
      setPausedUntil("");
    } catch (err: any) {
      toast.error(err?.message || "Något gick fel");
    } finally {
      setActionBusy(false);
    }
  };

  const handleResume = async () => {
    if (!subscription) return;
    setActionBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("resume-subscription", {
        body: { subscription_id: subscription.id },
      });
      if (error || !data?.success) throw new Error(error?.message || data?.error || "Misslyckades");
      if (user) await refreshAll(user.id);
      toast.success("Prenumerationen är aktiv igen");
    } catch (err: any) {
      toast.error(err?.message || "Något gick fel");
    } finally {
      setActionBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!subscription) return;
    setActionBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("cancel-subscription", {
        body: { subscription_id: subscription.id },
      });
      if (error || !data?.success) throw new Error(error?.message || data?.error || "Misslyckades");
      // Refresh from DB so subscription card disappears AND any cancelled
      // pending orders drop out of the list — no page reload needed.
      if (user) await refreshAll(user.id);
      toast.success("Prenumerationen avslutas vid periodens slut");
      setCancelOpen(false);
    } catch (err: any) {
      toast.error(err?.message || "Något gick fel");
    } finally {
      setActionBusy(false);
    }
  };

  const handleClearPending = async () => {
    if (!subscription) return;
    setActionBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("clear-pending-adjustment", {
        body: { subscription_id: subscription.id },
      });
      if (error || !data?.success) throw new Error(error?.message || data?.error || "Misslyckades");
      if (user) await refreshAll(user.id);
      toast.success("Den planerade ändringen är borttagen");
    } catch (err: any) {
      toast.error(err?.message || "Något gick fel");
    } finally {
      setActionBusy(false);
    }
  };

  const handleUpdateCard = async () => {
    setActionBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("update-payment-method", { body: {} });
      if (error || !data?.url) throw new Error(error?.message || data?.error || "Kunde inte starta uppdatering");
      window.location.href = data.url as string;
    } catch (err: any) {
      toast.error(err?.message || "Något gick fel");
      setActionBusy(false);
    }
  };

  if (loading) return <Layout><div className="min-h-screen" /></Layout>;

  const initials = user?.email ? user.email.slice(0, 2).toUpperCase() : "??";
  const name = user?.firstName || user?.email?.split("@")[0] || "du";

  if (view === "adjust" && subscription) {
    return (
      <Layout>
        <SubscriptionAdjuster
          subscriptionId={subscription.id}
          initialQuantities={subscription.quantities}
          initialTubes={subscription.tier_tubes}
          pendingEffectiveDate={subscription.pending_effective_date}
          mode="subscription"
          onBack={() => setView("overview")}
          onSaved={async () => {
            if (user) await refreshAll(user.id);
            setView("overview");
          }}
        />
      </Layout>
    );
  }

  const tier = subscription ? getExactTier(subscription.tier_tubes) : null;
  const monthlyPrice = subscription?.monthly_price_cents != null
    ? Math.round(subscription.monthly_price_cents / 100)
    : tier?.subscriptionPrice;

  const productNameById = (id: string) =>
    PRODUCTS.find((p) => p.id === id)?.name || id;

  const status = subscription
    ? statusBadge(subscription.status, subscription.cancel_at_period_end)
    : null;

  // Build pause options: customer chooses a RESUME MONTH (current+2 … current+3).
  // The value stored in `pausedUntil` is the LAST day of the month BEFORE the
  // chosen resume month (matches what pause-subscription expects).
  const pauseOptions = (() => {
    const today = new Date();
    const stockholmStr = today.toLocaleDateString("sv-SE", { timeZone: "Europe/Stockholm" });
    const [y, m] = stockholmStr.split("-").map(Number); // m is 1-indexed
    const monthNames = [
      "januari", "februari", "mars", "april", "maj", "juni",
      "juli", "augusti", "september", "oktober", "november", "december",
    ];
    const opts: Array<{ value: string; resumeLabel: string; firstMonday: string; skippedMonths: string[] }> = [];
    for (const offset of [2, 3]) {
      const resumeMonthZero = (m - 1) + offset;
      const resumeYear = y + Math.floor(resumeMonthZero / 12);
      const rmz = ((resumeMonthZero % 12) + 12) % 12;
      // paused_until = last day of the month before resume month
      const pauseEndMonthZero = resumeMonthZero - 1;
      const pauseEndYear = y + Math.floor(pauseEndMonthZero / 12);
      const pemz = ((pauseEndMonthZero % 12) + 12) % 12;
      const lastDay = new Date(Date.UTC(pauseEndYear, pemz + 1, 0));
      const value = lastDay.toISOString().slice(0, 10);
      // First Monday of resume month
      const first = new Date(Date.UTC(resumeYear, rmz, 1));
      const isoDow = ((first.getUTCDay() + 6) % 7) + 1;
      const fmOffset = (8 - isoDow) % 7;
      first.setUTCDate(first.getUTCDate() + fmOffset);
      const firstMondayIso = first.toISOString().slice(0, 10);
      // Skipped months = from current month through pause-end month
      const skipped: string[] = [];
      for (let i = 0; i <= offset - 1; i++) {
        const skipMonthZero = (m - 1) + i;
        const skipYear = y + Math.floor(skipMonthZero / 12);
        const smz = ((skipMonthZero % 12) + 12) % 12;
        skipped.push(`${monthNames[smz]} ${skipYear}`);
      }
      opts.push({
        value,
        resumeLabel: `${monthNames[rmz].charAt(0).toUpperCase()}${monthNames[rmz].slice(1)} ${resumeYear}`,
        firstMonday: firstMondayIso,
        skippedMonths: skipped,
      });
    }
    return opts;
  })();

  const selectedPauseOption = pauseOptions.find((o) => o.value === pausedUntil);

  // En redan betald leverans som ligger kvar i packkön. Den skickas även om
  // kunden pausar — pausen gäller från nästa månad.
  const paidPendingOrder = orders.find(
    (o) => o.status === "pending" && o.payment_status === "paid",
  );

  return (
    <Layout>
      <section className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-24">
          <div className="grid md:grid-cols-12 gap-10">
            {/* Sidebar */}
            <div className="md:col-span-4 lg:col-span-3 space-y-8">
              <div className="flex items-center gap-4 md:flex-col md:items-start">
                <div className="w-16 h-16 rounded-full bg-foreground text-background flex items-center justify-center text-lg font-bold shrink-0">
                  {initials}
                </div>
                <div>
                  <p className="font-serif italic text-2xl text-foreground">{getGreeting()}</p>
                  <h1 className="text-lg font-bold uppercase tracking-wider text-foreground">{name}</h1>
                </div>
              </div>

              <div className="border-t border-border pt-6 space-y-3">
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="border border-foreground bg-foreground text-background py-2.5 px-5 rounded-lg font-medium uppercase tracking-widest text-[10px] hover:bg-transparent hover:text-foreground transition-all duration-300 inline-flex items-center gap-2"
                  >
                    <ShieldCheck className="w-3.5 h-3.5" /> Admin-portal <ArrowRight className="w-3 h-3" />
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="border border-foreground text-foreground bg-transparent py-2.5 px-5 rounded-lg font-medium uppercase tracking-widest text-[10px] hover:bg-foreground hover:text-background transition-all duration-300 inline-flex items-center gap-2 block"
                >
                  <LogOut className="w-3.5 h-3.5" /> Logga ut <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            </div>

            {/* Main */}
            <div className="md:col-span-8 lg:col-span-9 space-y-12">
              {/* Failed card payment warning */}
              {orders.some((o) => o.payment_status === "failed" && o.payment_method === "card") && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-5 flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <p className="font-bold text-rose-900 text-sm">Betalningen misslyckades</p>
                    <p className="text-xs text-rose-900/80 mt-1">
                      Vi kunde inte dra betalningen från ditt kort. Uppdatera dina kortuppgifter för att undvika avbrott i leveranserna.
                    </p>
                  </div>
                  <button
                    onClick={handleUpdateCard}
                    disabled={actionBusy}
                    className="border border-rose-300 text-rose-900 py-2 px-4 rounded-lg font-medium uppercase tracking-widest text-[10px] hover:bg-rose-100 transition-all disabled:opacity-50 shrink-0"
                  >
                    Uppdatera kort
                  </button>
                </div>
              )}

              {/* Quick actions */}
              <div>
                <div className="mb-6">
                  <span className="font-serif italic text-2xl text-foreground">Ditt</span>
                  <h2 className="text-lg font-bold uppercase tracking-wider text-foreground">Konto</h2>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Link to="/pricing" className="bg-card rounded-xl p-6 flex items-start gap-4 hover:bg-muted/50 transition-all">
                    <ShoppingCart className="w-6 h-6 text-terracotta-500 shrink-0" />
                    <div>
                      <p className="font-bold text-foreground text-sm">Ny beställning</p>
                      <p className="text-[10px] text-muted-foreground mt-1 uppercase tracking-wider">Beställ dispenser eller refills</p>
                    </div>
                  </Link>
                  <a href="#orders" className="bg-card rounded-xl p-6 flex items-start gap-4 hover:bg-muted/50 transition-all">
                    <Package className="w-6 h-6 text-terracotta-500 shrink-0" />
                    <div>
                      <p className="font-bold text-foreground text-sm">Beställningshistorik</p>
                      <p className="text-[10px] text-muted-foreground mt-1 uppercase tracking-wider">{orders.length} beställning{orders.length === 1 ? "" : "ar"}</p>
                    </div>
                  </a>
                </div>
              </div>

              {/* Active subscription */}
              <div>
                <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
                  <div>
                    <span className="font-serif italic text-2xl text-foreground">Din</span>
                    <h2 className="text-lg font-bold uppercase tracking-wider text-foreground">
                      {subscriptions.length > 1 ? "Prenumerationer" : "Prenumeration"}
                    </h2>
                  </div>
                  {subscriptions.length > 1 && subscription && (
                    <div className="flex flex-wrap gap-2">
                      {subscriptions.map((s) => (
                        <button
                          key={s.id}
                          onClick={() => { setSubscription(s); setEditingNickname(false); }}
                          className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest border transition-all ${
                            subscription.id === s.id
                              ? "bg-foreground text-background border-foreground"
                              : "bg-transparent text-foreground border-foreground/20 hover:border-foreground/60"
                          }`}
                        >
                          {s.nickname || `Anläggning ${subscriptions.indexOf(s) + 1}`}
                          <span className="ml-1.5 opacity-60">{s.tier_tubes}t</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {subscription ? (
                  <div className="bg-card rounded-2xl p-8 space-y-6">
                    <div className="-mb-2 flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
                      {editingNickname ? (
                        <>
                          <input
                            autoFocus
                            value={nicknameDraft}
                            onChange={(e) => setNicknameDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveNickname();
                              if (e.key === "Escape") setEditingNickname(false);
                            }}
                            maxLength={80}
                            placeholder="Namn på anläggningen"
                            className="bg-transparent border-b border-foreground/30 focus:border-foreground outline-none text-[10px] uppercase tracking-widest text-foreground py-0.5 min-w-[180px]"
                          />
                          <button onClick={saveNickname} className="text-foreground hover:opacity-70" aria-label="Spara">
                            <CheckIcon className="w-3 h-3" />
                          </button>
                          <button onClick={() => setEditingNickname(false)} className="text-muted-foreground hover:text-foreground" aria-label="Avbryt">
                            <X className="w-3 h-3" />
                          </button>
                        </>
                      ) : (
                        <>
                          <span>{subscription.nickname || "Namnlös anläggning"}</span>
                          {subscription.is_pilot && (
                            <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-800 text-[9px] uppercase tracking-wider font-medium">
                              Pilot
                            </span>
                          )}
                          <button
                            onClick={() => {
                              setNicknameDraft(subscription.nickname || "");
                              setEditingNickname(true);
                            }}
                            className="text-muted-foreground hover:text-foreground"
                            aria-label="Byt namn"
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                        </>
                      )}
                    </div>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      {(() => {
                        const hasPending =
                          subscription.pending_effective_date &&
                          subscription.pending_tier_tubes != null;
                        const displayTubes = hasPending
                          ? subscription.pending_tier_tubes!
                          : subscription.tier_tubes;
                        const displayTier = getExactTier(displayTubes);
                        const displayPrice = hasPending
                          ? subscription.pending_monthly_price_cents != null
                            ? Math.round(subscription.pending_monthly_price_cents / 100)
                            : null
                          : monthlyPrice;
                        return (
                          <>
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-foreground/5 flex items-center justify-center">
                                <Sparkles className="w-4 h-4 text-foreground" />
                              </div>
                              <div>
                                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                  {hasPending ? "Justerat paket" : "Aktivt paket"}
                                </p>
                                <p className="text-lg font-bold uppercase tracking-wider text-foreground">
                                  {displayTier?.name || "Custom"} — {displayTubes} tuber
                                </p>
                              </div>
                            </div>
                            {displayPrice != null && (() => {
                              // Apply forever-discount to the displayed monthly price.
                              // 'once' discounts only count for first month so we never
                              // show them as ongoing here.
                              const isForever = subscription.discount_duration_snapshot === "forever";
                              const baseCents = displayPrice * 100;
                              let finalCents = baseCents;
                              let savingsCents = 0;
                              if (
                                isForever &&
                                subscription.discount_type_snapshot &&
                                subscription.discount_value_snapshot != null
                              ) {
                                if (subscription.discount_type_snapshot === "percent") {
                                  savingsCents = Math.round(
                                    (baseCents * subscription.discount_value_snapshot) / 100,
                                  );
                                } else {
                                  savingsCents = Math.min(
                                    subscription.discount_value_snapshot,
                                    baseCents,
                                  );
                                }
                                finalCents = baseCents - savingsCents;
                              }
                              const finalKr = Math.round(finalCents / 100);
                              return (
                                <div className="text-right">
                                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">per månad</p>
                                  {savingsCents > 0 ? (
                                    <>
                                      <p className="text-sm text-muted-foreground line-through tabular-nums">
                                        {displayPrice} kr
                                      </p>
                                      <p className="text-2xl font-bold text-foreground tabular-nums">
                                        {finalKr} kr
                                      </p>
                                    </>
                                  ) : (
                                    <p className="text-2xl font-bold text-foreground tabular-nums">
                                      {displayPrice} kr
                                    </p>
                                  )}
                                </div>
                              );
                            })()}
                          </>
                        );
                      })()}
                    </div>

                    {/* Active discount badge */}
                    {subscription.discount_id && subscription.discount_duration_snapshot && (
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                          <Sparkles className="w-3.5 h-3.5 text-emerald-700" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] uppercase tracking-widest text-emerald-900/70">
                            Aktiv rabatt
                          </p>
                          <p className="text-sm font-bold text-emerald-900 mt-0.5">
                            {subscription.discount?.code || subscription.discount?.name || "Rabatt"}
                            {subscription.discount_type_snapshot === "percent" &&
                              subscription.discount_value_snapshot != null && (
                                <span className="font-normal text-emerald-900/80">
                                  {" "}— {subscription.discount_value_snapshot}% rabatt
                                </span>
                              )}
                            {subscription.discount_type_snapshot === "fixed_amount" &&
                              subscription.discount_value_snapshot != null && (
                                <span className="font-normal text-emerald-900/80">
                                  {" "}— {Math.round(subscription.discount_value_snapshot / 100)} kr rabatt
                                </span>
                              )}
                          </p>
                          <p className="text-[11px] text-emerald-900/70 mt-1">
                            {subscription.discount_duration_snapshot === "forever"
                              ? "Gäller varje månad så länge prenumerationen är aktiv."
                              : "Användes vid signup — månad 2+ är fullpris."}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Pending adjustment banner */}
                    {subscription.pending_effective_date && subscription.pending_tier_tubes != null && (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 space-y-3">
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                          <div className="flex-1 min-w-[220px]">
                            <p className="font-bold uppercase tracking-wider text-[10px] text-amber-900 mb-2">
                              Nästa leverans uppdateras
                            </p>
                            <div className="flex items-center gap-3 flex-wrap">
                              <div className="text-xs text-amber-900/70">
                                <p className="text-[10px] uppercase tracking-widest mb-0.5">Från</p>
                                <p className="font-bold text-sm line-through decoration-amber-900/40">
                                  {subscription.tier_tubes} tuber
                                  {monthlyPrice != null && <> — {monthlyPrice} kr/mån</>}
                                </p>
                              </div>
                              <ArrowRight className="w-4 h-4 text-amber-900/60 shrink-0" />
                              <div className="text-xs text-amber-900">
                                <p className="text-[10px] uppercase tracking-widest mb-0.5">Till</p>
                                <p className="font-bold text-sm">
                                  {subscription.pending_tier_tubes} tuber
                                  {subscription.pending_monthly_price_cents != null && (
                                    <> — {Math.round(subscription.pending_monthly_price_cents / 100)} kr/mån</>
                                  )}
                                </p>
                              </div>
                            </div>
                            <p className="text-[11px] text-amber-900/80 mt-3">
                              Det justerade paketet skickas med din nästa leverans den{" "}
                              <strong>{formatLongDate(subscription.pending_effective_date)}</strong>.
                            </p>
                          </div>
                          <button
                            onClick={handleClearPending}
                            disabled={actionBusy}
                            className="text-[10px] uppercase tracking-widest text-amber-900 underline hover:no-underline disabled:opacity-50 shrink-0"
                          >
                            Ångra ändring
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Cancelled-at-period-end banner */}
                    {subscription.cancel_at_period_end && subscription.status !== "cancelled" && (
                      <div className="rounded-xl p-4 text-xs bg-rose-50 border border-rose-200 text-rose-900">
                        <p className="font-bold uppercase tracking-wider text-[10px] mb-1">
                          Uppsagd
                        </p>
                        <p>
                          {subscription.current_period_end ? (
                            <>
                              Prenumerationen avslutas{" "}
                              <span className="font-semibold">
                                {formatLongDate(subscription.current_period_end)}
                              </span>
                              . Fram till dess gäller allt som vanligt — du kan ångra dig när som
                              helst.
                            </>
                          ) : (
                            <>
                              Prenumerationen avslutas vid periodens slut. Fram till dess gäller
                              allt som vanligt — du kan ångra dig när som helst.
                            </>
                          )}
                        </p>
                      </div>
                    )}

                    {/* Paused banner */}
                    {subscription.status === "paused" && (
                      <div className={`rounded-xl p-4 text-xs ${subscription.paused_for_overdue ? "bg-rose-50 border border-rose-200 text-rose-900" : "bg-amber-50 border border-amber-200 text-amber-900"}`}>
                        <p className="font-bold uppercase tracking-wider text-[10px] mb-1">
                          {subscription.paused_for_overdue ? "Pausad – obetald faktura" : "Pausad"}
                        </p>
                        <p>
                          {subscription.paused_for_overdue
                            ? <>Prenumerationen är pausad på grund av en obetald faktura. Kontakta oss på <a href="mailto:info@aquadias.se" className="underline">info@aquadias.se</a> så hjälper vi dig.</>
                            : subscription.next_delivery_date
                              ? <>Nästa leverans skickas <span className="font-semibold">{formatLongDate(subscription.next_delivery_date)}</span>.</>
                              : subscription.paused_until
                                ? <>Återupptas automatiskt efter {formatLongDate(subscription.paused_until)}.</>
                                : <>Pausad. Återuppta när du vill.</>}
                        </p>
                        {!subscription.paused_for_overdue && paidPendingOrder && (
                          <p className="mt-2 font-semibold">
                            Din betalda leverans från {formatLongDate(paidPendingOrder.created_at)} skickas
                            som planerat, trots pausen.
                          </p>
                        )}

                      </div>
                    )}


                    <div className="border-t border-border pt-6 grid sm:grid-cols-2 gap-4 text-xs">
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Nästa leverans</p>
                        <p className="font-medium text-foreground mt-1">
                          {subscription.next_delivery_date
                            ? formatDate(subscription.next_delivery_date)
                            : "—"}
                        </p>
                        {subscription.next_delivery_date && (
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            Levereras {deliveryWindowLabel(subscription.next_delivery_date)} (5–7 arbetsdagar)
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Status</p>
                        {status && (
                          <span className={`inline-block mt-1 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider rounded ${status.className}`}>
                            {status.label}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="border-t border-border pt-6 grid sm:grid-cols-2 gap-2">
                      {Object.entries(subscription.quantities).filter(([_, q]) => q > 0).map(([id, q]) => (
                        <div key={id} className="flex items-center gap-2 text-xs">
                          <span className="bg-foreground text-background text-[10px] font-bold w-5 h-5 rounded-full inline-flex items-center justify-center">{q}</span>
                          <span className="text-foreground">{productNameById(id)}</span>
                        </div>
                      ))}
                    </div>

                    <div className="border-t border-border pt-6 flex flex-wrap gap-3">
                      {subscription.status === "active" && !subscription.cancel_at_period_end && (
                        <button
                          onClick={() => setView("adjust")}
                          className="bg-foreground text-background py-3 px-6 rounded-lg font-medium uppercase tracking-widest text-[10px] hover:bg-foreground/90 transition-all duration-300 inline-flex items-center gap-2"
                        >
                          Justera mitt paket <ArrowRight className="w-3 h-3" />
                        </button>
                      )}
                      {subscription.status === "active" && !subscription.cancel_at_period_end && (
                        <button
                          onClick={() => setPauseOpen(true)}
                          className="border border-foreground text-foreground bg-transparent py-3 px-5 rounded-lg font-medium uppercase tracking-widest text-[10px] hover:bg-foreground hover:text-background transition-all duration-300 inline-flex items-center gap-2"
                        >
                          <Pause className="w-3 h-3" /> Pausa
                        </button>
                      )}
                      {subscription.cancel_at_period_end && subscription.status !== "cancelled" && (
                        <button
                          onClick={handleResume}
                          disabled={actionBusy}
                          className="bg-foreground text-background py-3 px-6 rounded-lg font-medium uppercase tracking-widest text-[10px] hover:bg-foreground/90 transition-all duration-300 inline-flex items-center gap-2 disabled:opacity-50"
                        >
                          <Play className="w-3 h-3" /> Återuppta abonnemang
                        </button>
                      )}
                      {subscription.status === "paused" && !subscription.cancel_at_period_end && !subscription.paused_for_overdue && (
                        <button
                          onClick={handleResume}
                          disabled={actionBusy}
                          className="bg-foreground text-background py-3 px-6 rounded-lg font-medium uppercase tracking-widest text-[10px] hover:bg-foreground/90 transition-all duration-300 inline-flex items-center gap-2 disabled:opacity-50"
                        >
                          <Play className="w-3 h-3" /> Återuppta
                        </button>
                      )}

                      <button
                        onClick={handleUpdateCard}
                        disabled={actionBusy}
                        className="border border-foreground text-foreground bg-transparent py-3 px-5 rounded-lg font-medium uppercase tracking-widest text-[10px] hover:bg-foreground hover:text-background transition-all duration-300 inline-flex items-center gap-2 disabled:opacity-50"
                      >
                        <CreditCard className="w-3 h-3" /> Uppdatera kort
                      </button>
                      {!subscription.cancel_at_period_end && (
                      <button
                        onClick={() => setCancelOpen(true)}
                        className="text-muted-foreground hover:text-rose-700 py-3 px-5 rounded-lg font-medium uppercase tracking-widest text-[10px] transition-all duration-300 inline-flex items-center gap-2"
                      >
                        <X className="w-3 h-3" /> Avsluta
                      </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-card rounded-2xl p-8 text-center">
                    <p className="text-sm text-muted-foreground">Du har ingen aktiv prenumeration ännu.</p>
                    <Link
                      to="/pricing?mode=subscription"
                      className="inline-flex items-center gap-2 bg-foreground text-background py-3 px-6 rounded-lg font-medium uppercase tracking-widest text-[10px] hover:bg-foreground/90 transition-all duration-300 mt-4"
                    >
                      Starta en prenumeration <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                )}
              </div>

              {/* Orders */}
              <div id="orders">
                <div className="mb-6">
                  <span className="font-serif italic text-2xl text-foreground">Dina</span>
                  <h2 className="text-lg font-bold uppercase tracking-wider text-foreground">Beställningar</h2>
                </div>
                {orders.length === 0 ? (
                  <div className="bg-card rounded-2xl p-8 text-center">
                    <p className="text-sm text-muted-foreground">Inga beställningar ännu.</p>
                  </div>
                ) : (
                  <div className="bg-card rounded-2xl divide-y divide-border overflow-hidden">
                    {orders.map((o) => {
                      const ps = formatOrderState(o.status, o.payment_status, o.payment_method);
                      const total = o.total_cents != null
                        ? `${(o.total_cents / 100).toFixed(0)} ${o.currency || "SEK"}`
                        : "—";
                      // Customer can resume/discard any non-confirmed order:
                      // awaiting_payment (never tried), failed (crashed mid-flow),
                      // cancelled (Stripe session expired). Confirmed orders
                      // (paid/pending/refunded) show a kvitto-link instead.
                      const isResumable =
                        o.payment_status === "awaiting_payment" ||
                        o.payment_status === "failed" ||
                        o.payment_status === "cancelled";
                      const needs3DS = o.payment_status === "requires_action";
                      const isResuming = resumingOrderId === o.id;
                      const handleResumeOrder = () => {
                        if (resumingOrderId) return;
                        setResumingOrderId(o.id);
                        resumeCheckout({
                          id: o.id,
                          order_mode: (o.order_mode as "purchase" | "subscription") || "purchase",
                          total_cents: o.total_cents,
                          items: o.items,
                          shipping_address: o.shipping_address,
                        });
                        // Release the lock after the sheet has had time to
                        // mount and own the flow. The sheet itself prevents
                        // further duplicate sessions from inside.
                        window.setTimeout(() => setResumingOrderId(null), 1500);
                      };
                      const handleConfirm3DS = async () => {
                        if (resumingOrderId) return;
                        setResumingOrderId(o.id);
                        try {
                          const { data, error } = await (supabase as any).functions.invoke(
                            "confirm-payment-intent",
                            { body: { order_id: o.id } },
                          );
                          if (error || !data?.client_secret) {
                            throw new Error(error?.message || "Kunde inte hämta bekräftelse");
                          }
                          const stripe = await getStripe();
                          if (!stripe) throw new Error("Stripe kunde inte laddas");
                          const result = await stripe.confirmCardPayment(data.client_secret);
                          if (result.error) {
                            toast.error(result.error.message || "Bekräftelsen misslyckades");
                          } else if (result.paymentIntent?.status === "succeeded") {
                            toast.success("Betalning bekräftad!");
                            if (user?.id) await fetchOrders(user.id);
                          } else {
                            toast.message("Bekräftelse pågår — uppdatera sidan om en stund.");
                          }
                        } catch (e: any) {
                          toast.error(e?.message || "Något gick fel");
                        } finally {
                          setResumingOrderId(null);
                        }
                      };
                      const handleDiscardOrder = async () => {
                        if (!confirm("Vill du ta bort den här ofullbordade beställningen?")) return;
                        const { error } = await (supabase as any)
                          .from("orders")
                          .delete()
                          .eq("id", o.id);
                        if (error) {
                          toast.error("Kunde inte ta bort beställningen");
                          return;
                        }
                        setOrders((prev) => prev.filter((x) => x.id !== o.id));
                        toast.success("Beställningen togs bort");
                      };
                      const resumeLabel = o.payment_status === "awaiting_payment"
                        ? "Slutför betalning"
                        : "Försök igen";
                      return (
                        <div key={o.id} className="p-5 md:p-6 flex flex-wrap items-center gap-4">
                          <div className="flex-1 min-w-[180px]">
                            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{formatDate(o.created_at)}</p>
                            <p className="text-sm font-semibold text-foreground mt-0.5">{o.product_name}</p>
                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
                              {o.order_mode === "subscription" ? "Prenumeration" : "Engångsköp"} · {o.quantity} tuber
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-base font-bold text-foreground tabular-nums">{total}</p>
                            <span className={`inline-block mt-1 px-2 py-0.5 text-[9px] font-medium uppercase tracking-wider rounded ${ps.className}`}>
                              {ps.label}
                            </span>
                            {ps.subtext && (
                              <p className="text-[9px] uppercase tracking-wider text-muted-foreground mt-1">
                                {ps.subtext}
                              </p>
                            )}
                          </div>
                          {isResumable && (
                            <div className="flex items-center gap-2 w-full sm:w-auto">
                              <button
                                onClick={handleResumeOrder}
                                disabled={isResuming}
                                aria-busy={isResuming}
                                className="bg-foreground text-background py-2 px-3 rounded-lg font-medium uppercase tracking-widest text-[9px] hover:bg-foreground/90 transition-all duration-300 inline-flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
                              >
                                <CreditCard className="w-3 h-3" /> {isResuming ? "Öppnar…" : resumeLabel}
                              </button>
                              <button
                                onClick={handleDiscardOrder}
                                className="border border-border text-muted-foreground bg-transparent py-2 px-3 rounded-lg font-medium uppercase tracking-widest text-[9px] hover:text-rose-700 hover:border-rose-300 transition-all duration-300 inline-flex items-center gap-1.5"
                                aria-label="Ta bort"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                          {needs3DS && (
                            <button
                              onClick={handleConfirm3DS}
                              disabled={isResuming}
                              aria-busy={isResuming}
                              className="bg-orange-600 text-white py-2 px-3 rounded-lg font-medium uppercase tracking-widest text-[9px] hover:bg-orange-700 transition-all duration-300 inline-flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                              <ShieldCheck className="w-3 h-3" /> {isResuming ? "Öppnar…" : "Bekräfta i banken"}
                            </button>
                          )}
                          {!isResumable && !needs3DS && o.receipt_url && (
                            <a
                              href={o.receipt_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="border border-foreground text-foreground bg-transparent py-2 px-3 rounded-lg font-medium uppercase tracking-widest text-[9px] hover:bg-foreground hover:text-background transition-all duration-300 inline-flex items-center gap-1.5"
                            >
                              <FileText className="w-3 h-3" /> Kvitto
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Support */}
                <div className="bg-card rounded-2xl p-6 md:p-8 mt-6">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-foreground">Behöver du hjälp?</h3>
                  <p className="text-sm text-muted-foreground mt-1 mb-5">
                    Reklamation, återbetalning eller fråga om en beställning — skriv till oss här så återkommer vi till din e-post.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <select
                      value={supportTopic}
                      onChange={(e) => setSupportTopic(e.target.value)}
                      className="h-11 rounded-lg border border-border bg-background px-3 text-sm text-foreground"
                    >
                      <option>Återbetalning / reklamation</option>
                      <option>Fråga om beställning</option>
                      <option>Fråga om faktura</option>
                      <option>Annat</option>
                    </select>
                    <select
                      value={supportOrderId}
                      onChange={(e) => setSupportOrderId(e.target.value)}
                      className="h-11 rounded-lg border border-border bg-background px-3 text-sm text-foreground"
                    >
                      <option value="">Gäller ingen specifik beställning</option>
                      {orders.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.product_name} · {formatDate(o.created_at)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <textarea
                    value={supportMessage}
                    onChange={(e) => setSupportMessage(e.target.value)}
                    rows={4}
                    placeholder="Beskriv ditt ärende…"
                    className="mt-3 w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground resize-y"
                  />
                  <button
                    onClick={sendSupportRequest}
                    disabled={supportSending}
                    className="mt-4 bg-foreground text-background py-3 px-6 rounded-lg font-medium uppercase tracking-widest text-[10px] hover:bg-foreground/90 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {supportSending ? "Skickar…" : "Skicka meddelande"}
                  </button>
                </div>
              </div>

              {/* Invoices */}
              <div id="invoices">
                <div className="mb-6 mt-12">
                  <span className="font-serif italic text-2xl text-foreground">Dina</span>
                  <h2 className="text-lg font-bold uppercase tracking-wider text-foreground">Fakturor</h2>
                </div>
                {invoices.length === 0 ? (
                  <div className="bg-card rounded-2xl p-8 text-center">
                    <p className="text-sm text-muted-foreground">Inga fakturor ännu.</p>
                  </div>
                ) : (
                  <div className="bg-card rounded-2xl divide-y divide-border overflow-hidden">
                    {invoices.map((inv) => {
                      const status = inv.stripe_status || inv.status;
                      const overdue = Boolean(
                        inv.due_date && status !== "paid" && status !== "void" &&
                        new Date(`${inv.due_date}T23:59:59`) < new Date(),
                      );
                      const badge = invoiceBadge(status, overdue);
                      const periodText = invoicePeriodLabel(inv);
                      const amount = inv.amount_cents != null
                        ? `${(inv.amount_cents / 100).toFixed(0)} ${inv.currency || "SEK"}`
                        : "—";
                      const isVoid = status === "void";
                      return (
                        <div
                          key={inv.id}
                          className={`p-5 md:p-6 flex flex-wrap items-center gap-4 ${
                            isVoid ? "opacity-60" : ""
                          }`}
                        >
                          <div className="flex-1 min-w-[180px]">
                            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                              {formatDate(inv.created_at)}
                              {periodText ? ` · ${periodText}` : ""}
                            </p>
                            <p className={`text-sm font-semibold text-foreground mt-0.5 ${isVoid ? "line-through" : ""}`}>
                              Faktura {inv.number || "—"}
                            </p>
                            {isVoid ? (
                              <p className="text-[10px] uppercase tracking-wider text-rose-700 mt-0.5 font-medium">
                                Makulerad – ska inte betalas. OCR-numret är avstängt.
                              </p>
                            ) : (
                              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
                                {inv.ocr_reference ? `OCR ${inv.ocr_reference}` : "OCR tilldelas när fakturan skickas"}
                                {inv.due_date ? ` · Förfaller ${inv.due_date}` : ""}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className={`text-base font-bold text-foreground tabular-nums ${isVoid ? "line-through" : ""}`}>{amount}</p>
                            <span className={`inline-block mt-1 px-2 py-0.5 text-[9px] font-medium uppercase tracking-wider rounded ${badge.className}`}>
                              {badge.label}
                            </span>
                          </div>
                          {status !== "draft" && (
                            <div className="flex flex-col items-end gap-1">
                              {(status === "open" || status === "uncollectible") && inv.hosted_invoice_url && (
                                <a
                                  href={inv.hosted_invoice_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="bg-foreground text-background py-2 px-3 rounded-lg font-medium uppercase tracking-widest text-[9px] hover:bg-foreground/90 transition-all duration-300"
                                >
                                  Betala nu
                                </a>
                              )}
                              <button
                                onClick={() => handleDownloadInvoice(inv)}
                                disabled={downloadingInvoiceId === inv.id}
                                className="border border-foreground text-foreground bg-transparent py-2 px-3 rounded-lg font-medium uppercase tracking-widest text-[9px] hover:bg-foreground hover:text-background transition-all duration-300 inline-flex items-center gap-1.5 disabled:opacity-50"
                              >
                                <FileText className="w-3 h-3" />
                                {downloadingInvoiceId === inv.id
                                  ? "Hämtar…"
                                  : isVoid
                                  ? "Ladda ner (makulerad)"
                                  : "Ladda ner PDF"}
                              </button>
                              {inv.hosted_invoice_url && (
                                <a
                                  href={inv.hosted_invoice_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[9px] uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
                                >
                                  Öppna hos Stripe
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      );

                    })}
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Pause dialog */}
      <AlertDialog open={pauseOpen} onOpenChange={(open) => {
        setPauseOpen(open);
        if (!open) setPausedUntil("");
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Pausa prenumeration</AlertDialogTitle>
            <AlertDialogDescription>
              Välj månad då du vill att din nästa leverans ska komma. Du får inga
              leveranser under perioden fram till dess.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-3 py-2">
            {pauseOptions.map((opt) => (
              <label
                key={opt.value}
                className={`flex items-start gap-3 cursor-pointer border rounded-lg p-3 transition-colors ${
                  pausedUntil === opt.value
                    ? "border-foreground bg-muted/40"
                    : "border-border hover:border-foreground/40"
                }`}
              >
                <input
                  type="radio"
                  name="pause-resume-month"
                  checked={pausedUntil === opt.value}
                  onChange={() => setPausedUntil(opt.value)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">
                    Återuppta i {opt.resumeLabel}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Nästa leverans: {formatLongDate(opt.firstMonday)}
                  </p>
                </div>
              </label>
            ))}

            {selectedPauseOption && (
              <div className="bg-muted/40 border border-border rounded-lg p-3 text-xs text-muted-foreground">
                <p>
                  Du får inga leveranser under{" "}
                  <span className="font-medium text-foreground">
                    {selectedPauseOption.skippedMonths.join(" och ")}
                  </span>
                  . Nästa leverans skickas{" "}
                  <span className="font-medium text-foreground">
                    {formatLongDate(selectedPauseOption.firstMonday)}
                  </span>
                  .
                </p>
              </div>
            )}

            {paidPendingOrder ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900">
                <p>
                  Din redan betalda leverans skickas som planerat — pausen börjar
                  gälla från nästa månad.
                </p>
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
                <p>
                  Har du en obetald faktura för den här månaden annulleras den när du
                  pausar, och ingen leverans skickas.
                </p>
              </div>
            )}

          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionBusy}>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePause}
              disabled={actionBusy || !pausedUntil}
            >
              {actionBusy ? "Pausar…" : "Pausa"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel dialog */}
      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Avsluta prenumeration?</AlertDialogTitle>
            <AlertDialogDescription>
              Prenumerationen avslutas vid periodens slut — leveransen du redan betalat för skickas
              som vanligt. Du kan ångra uppsägningen fram till dess.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionBusy}>Behåll</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              disabled={actionBusy}
              className="bg-rose-600 hover:bg-rose-700"
            >
              {actionBusy ? "Avslutar…" : "Ja, avsluta"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default AccountPage;
